import * as THREE from 'three';
import gsap from 'gsap';
import { settings } from '@/config';

/**
 * igloo.inc-style intro reveal. Every scene material is patched (via
 * onBeforeCompile) with a dissolve: fragments appear bottom-up with an organic
 * noise edge and a glowing leading line — like the mesh's outline sweeping across
 * before the surface fills in. A single shared `progress` uniform drives all
 * materials and is animated 0 → 1 with GSAP.
 *
 * Technique reference: shader-based dissolve / reveal (igloo.inc, Codrops).
 */
export class RevealEffect {
  /** Shared uniform object (same reference assigned to every patched material).
   *  Starts below 0 so the noise edge can't leak specks before the reveal plays. */
  readonly progress = { value: -0.25 };
  onComplete?: () => void;

  private min = 0;
  private max = 1;
  private readonly edgeColor: THREE.Color;
  private readonly patched = new Set<THREE.Material>();

  constructor() {
    this.edgeColor = new THREE.Color(settings.intro.edgeColor);
  }

  /**
   * Patch every material in the given subtree(s) and capture the COMBINED vertical bounds.
   *
   * Accepts an array so a set of loose meshes (e.g. everything bucketed into model scene 1) can
   * be revealed as ONE body: the dissolve sweeps bottom-up across their shared bounding box,
   * instead of each mesh restarting the sweep against its own bounds.
   */
  register(roots: THREE.Object3D | THREE.Object3D[]): void {
    const list = Array.isArray(roots) ? roots : [roots];
    if (!list.length) return;

    const box = new THREE.Box3();
    for (const root of list) box.union(new THREE.Box3().setFromObject(root));
    this.min = box.min.y;
    this.max = box.max.y;

    for (const root of list) root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) if (material) this.patch(material);
    });
  }

  private patch(material: THREE.Material): void {
    if (this.patched.has(material)) return;
    this.patched.add(material);

    const min = this.min;
    const max = this.max;

    // Chain over any existing onBeforeCompile (e.g. a hue/saturation shift already
    // patched onto this material in `orvaeScene.ts`) instead of clobbering it —
    // same convention as `HeightFog`, which chains over THIS patch in turn.
    const prev = material.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      prev?.(shader, renderer);
      shader.uniforms.uReveal = this.progress;
      shader.uniforms.uRevealMin = { value: min };
      shader.uniforms.uRevealMax = { value: max };
      shader.uniforms.uRevealEdge = { value: this.edgeColor };
      shader.uniforms.uRevealEdgeI = { value: settings.intro.edgeIntensity };
      shader.uniforms.uRevealNoise = { value: settings.intro.noise };

      shader.vertexShader =
        'varying vec3 vRevealW;\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n  vRevealW = (modelMatrix * vec4(transformed, 1.0)).xyz;',
        );

      shader.fragmentShader =
        REVEAL_HEAD +
        shader.fragmentShader
          .replace(
            '#include <clipping_planes_fragment>',
            '#include <clipping_planes_fragment>\n' + REVEAL_CLIP,
          )
          .replace('#include <dithering_fragment>', REVEAL_EDGE + '\n#include <dithering_fragment>');
    };
    material.needsUpdate = true;
  }

  /** Hide everything, then dissolve in. Call once, after warm-up. */
  play(): void {
    this.progress.value = -0.25;
    gsap.fromTo(
      this.progress,
      { value: -0.25 },
      {
        value: 1.15,
        duration: settings.intro.duration,
        delay: settings.intro.delay,
        ease: 'power2.inOut',
        onComplete: () => this.onComplete?.(),
      },
    );
  }

  dispose(): void {
    gsap.killTweensOf(this.progress);
    this.patched.clear();
  }
}

const REVEAL_HEAD = /* glsl */ `
  uniform float uReveal;
  uniform float uRevealMin;
  uniform float uRevealMax;
  uniform vec3 uRevealEdge;
  uniform float uRevealEdgeI;
  uniform float uRevealNoise;
  varying vec3 vRevealW;

  float revHash(vec2 p) {
    p = fract(p * vec2(123.34, 345.45));
    p += dot(p, p + 34.345);
    return fract(p.x * p.y);
  }
  float revNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = revHash(i);
    float b = revHash(i + vec2(1.0, 0.0));
    float c = revHash(i + vec2(0.0, 1.0));
    float d = revHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
`;

const REVEAL_CLIP = /* glsl */ `
  float gRev = (vRevealW.y - uRevealMin) / max(uRevealMax - uRevealMin, 1e-4);
  float gN = (revNoise(vRevealW.xz * 2.5) - 0.5) * uRevealNoise
           + (revNoise(vRevealW.xz * 8.0) - 0.5) * uRevealNoise * 0.4;
  float gThresh = gRev + gN;
  if (gThresh > uReveal) discard;
  float gEdge = 1.0 - smoothstep(0.0, 0.09, uReveal - gThresh);
`;

const REVEAL_EDGE = /* glsl */ `
  gl_FragColor.rgb += uRevealEdge * (gEdge * uRevealEdgeI);
`;
